import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import { addDays, startOfWeek } from 'date-fns'
import { camelCase } from 'lodash'
import { setStore, getStore, userRemove } from '../utils/auth'
import { useTranslation } from 'react-i18next'
import { LANGUAGE_LIST, DEFAULT_LANG } from '../i18n/utils'
import { DAY_OF_WEEK, DATE_FORMAT_WITH_WEEK } from '../utils/constant'

/**
 * Sync state to local storage so that it persists through a page refresh.
 * Usage is similar to useState except we pass in a local storage key so that
 * we can default to that value on page load instead of the specified initial value.
 *
 * @param { string } key
 * @param { any } initialValue
 * @return { array }
 */
const useLocalStorage = (key, initialValue) => {
  const { pathname } = useLocation()
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const getValue = () => {
    try {
      return getStore(key) || initialValue
    } catch (error) {
      // If error also return initialValue
      return initialValue
    }
  }

  const [storedValue, setStoredValue] = useState(getValue)
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value
      // Save state
      setStoredValue(valueToStore)
      // Save to local storage
      setStore(key, valueToStore)
      if (!value) userRemove()
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.log(error)
    }
  }

  useEffect(() => {
    !storedValue && setStoredValue(getValue)
  }, [pathname])
  return [storedValue, setValue]
}

const useCustomTranslation = () => {
  const { i18n } = useTranslation()
  const currentLangObj = LANGUAGE_LIST.find((language) => language.lang === (i18n.language || DEFAULT_LANG))
  const currentShotLang = currentLangObj?.shotLang
  const tB = (key) => {
    return camelCase(`${key}-${currentShotLang}`)
  }

  return [tB]
}

const useImageTranslation = () => {
  const { i18n } = useTranslation()
  const languagesSuffix = LANGUAGE_LIST.map((language) => language.suffix)
  const currentLangObj = LANGUAGE_LIST.find((language) => language.lang === (i18n.language || DEFAULT_LANG))
  let nullImagesSuffix = []

  const tImage = (key, format) => {
    let result = null
    const getImage = (suffix) => {
      try {
        const image = require(`src/assets/images/${key}_${suffix}.${format}`)
        result = image?.default
      } catch (error) {
        nullImagesSuffix.push(suffix)
        const nextSuffix = languagesSuffix?.find(
          (languageSuffix) =>
            !nullImagesSuffix?.find((nullImageSuffix) => nullImageSuffix === languageSuffix),
        )
        if (!nextSuffix) return result
        getImage(nextSuffix)
      }
    }
    getImage(currentLangObj?.suffix)

    return result
  }

  return [tImage]
}

const useDateFnsLocale = () => {
  const { i18n } = useTranslation()
  const currentLangObj = LANGUAGE_LIST.find((language) => language.lang === (i18n.language || DEFAULT_LANG))
  const currentDateFnsLocale = currentLangObj?.dateFns

  const formatWeek = (week) =>
    format(parse(week, DAY_OF_WEEK, new Date()), DAY_OF_WEEK, {
      locale: currentDateFnsLocale,
    })

  const formatDate = (date, formatStr = DATE_FORMAT_WITH_WEEK) =>
    format(date, formatStr, {
      locale: currentDateFnsLocale,
    })

  const firstDOW = startOfWeek(new Date())
  const weekDaysArray = (pattern = 'EEEE') =>
    Array.from(Array(7)).map((e, i) =>
      format(addDays(firstDOW, i), pattern, {
        locale: currentDateFnsLocale,
      }),
    )

  return { formatWeek, formatDate, weekDaysArray }
}

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

const useTranslateFormErrors = (errors, touched, setFieldTouched) => {
  const { i18n } = useTranslation()

  useEffect(() => {
    Object.keys(errors).forEach((fieldName) => {
      if (Object.keys(touched).includes(fieldName)) {
        setFieldTouched(fieldName)
      }
    })
  }, [i18n.language])
}

const useAddressFormat = () => {
  const { i18n } = useTranslation()
  const addressFormat = (address, area) =>
    `${i18n.language === 'en' ? '' : area}${address} ${i18n.language !== 'en' ? '' : area}`
  return { addressFormat }
}

export {
  useLocalStorage,
  useCustomTranslation,
  useImageTranslation,
  useDateFnsLocale,
  useQuery,
  useTranslateFormErrors,
  useAddressFormat,
}
